import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  DescriptionDetails,
  DescriptionItem,
  DescriptionList,
  DescriptionTerm,
  Separator,
  TextHeading,
  TextMono,
  TextSans,
} from "raft-ui";
import { getAuthMe } from "../lib/api";
import { AppearanceSettings } from "../components/AppearanceSettings";

function OrgRoleBadge({ role }: { role: string | null }) {
  if (!role) return <>—</>;
  if (role === "owner") return <Badge variant="accent">{role}</Badge>;
  if (role === "admin") return <Badge variant="information">{role}</Badge>;
  return <>{role}</>;
}

export function Settings() {
  const me = useQuery({ queryKey: ["auth-me"], queryFn: () => getAuthMe() });
  const account = me.data?.account;
  const raftCallbackUrl = `${window.location.origin}/login/raft/callback`;

  return (
    <div className="space-y-6">
      <TextHeading level={1} className="text-2xl leading-8">
        Settings
      </TextHeading>

      {/* Current account + org context */}
      {account && (
        <section className="border-t border-line-hairline pt-5">
          <h3 className="mb-3 text-sm font-medium text-foreground-strong">
            Current account
          </h3>
          <div className="space-y-4">
            <DescriptionList direction="horizontal">
              <DescriptionItem>
                <DescriptionTerm>Display name</DescriptionTerm>
                <DescriptionDetails>{account.display_name}</DescriptionDetails>
              </DescriptionItem>
              <DescriptionItem>
                <DescriptionTerm>Principal type</DescriptionTerm>
                <DescriptionDetails>
                  {account.principal_type === "agent" ? (
                    <Badge variant="accent">agent (Raft)</Badge>
                  ) : account.principal_type === "human" ? (
                    <>human (Raft)</>
                  ) : (
                    <>{account.principal_type}</>
                  )}
                </DescriptionDetails>
              </DescriptionItem>
              <DescriptionItem>
                <DescriptionTerm>Server</DescriptionTerm>
                <DescriptionDetails className="font-mono">
                  {account.server_slug ?? account.server_id}
                </DescriptionDetails>
              </DescriptionItem>
              <DescriptionItem>
                <DescriptionTerm>Server role (from Raft)</DescriptionTerm>
                <DescriptionDetails>
                  {account.server_role ?? "—"}
                </DescriptionDetails>
              </DescriptionItem>
              <DescriptionItem>
                <DescriptionTerm>Org id</DescriptionTerm>
                <DescriptionDetails className="font-mono">
                  {account.org_id ?? "—"}
                </DescriptionDetails>
              </DescriptionItem>
              <DescriptionItem>
                <DescriptionTerm>Your org role</DescriptionTerm>
                <DescriptionDetails>
                  <OrgRoleBadge role={account.org_role} />
                </DescriptionDetails>
              </DescriptionItem>
            </DescriptionList>
            <Separator />
            <TextSans size="caption">
              Signed in with Raft. To change role, ask an organization owner or
              admin in Organization settings.
            </TextSans>
          </div>
        </section>
      )}

      {/* Appearance: RUI theme family + mode; persisted per browser */}
      <section className="border-t border-line-hairline pt-5">
        <h3 className="mb-1 text-sm font-medium text-foreground-strong">
          Appearance
        </h3>
        <p className="mb-3 text-sm text-foreground-muted">
          Choose how the dashboard looks. Preferences are saved in this
          browser.
        </p>
        <AppearanceSettings />
      </section>

      {/* Infrastructure (existing static info) */}
      <section className="border-t border-line-hairline pt-5">
        <h3 className="mb-3 text-sm font-medium text-foreground-strong">
          Infrastructure
        </h3>
        <div className="space-y-3">
          <TextSans size="small">
            Admin access uses Login with Raft. Access follows your Raft
            organization membership and role.
          </TextSans>
          <div className="space-y-1">
            <TextSans size="caption">Raft Callback URL</TextSans>
            <TextMono size="code" className="break-all">
              {raftCallbackUrl}
            </TextMono>
          </div>
        </div>
      </section>
    </div>
  );
}
